/**
 * OIDC 認可フローのセキュリティパラメータ生成ユーティリティ
 *
 * CR-006 対応: 認可開始時に state / nonce / PKCE(code_verifier・code_challenge) を生成し、
 * CSRF・IDトークンリプレイ・認可コード横取りを防止する。
 * 生成値は sessionStorage に保存し、コールバックで突合・利用する。
 */

/** バイト列を base64url（RFC 4648 §5、パディングなし）でエンコード */
function base64UrlEncode(bytes: Uint8Array): string {
  let binary = '';
  bytes.forEach((b) => {
    binary += String.fromCharCode(b);
  });
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/** 暗号学的乱数から base64url 文字列を生成 */
function randomUrlSafe(byteLength: number): string {
  const bytes = new Uint8Array(byteLength);
  crypto.getRandomValues(bytes);
  return base64UrlEncode(bytes);
}

/** SHA-256 ハッシュを base64url で返す（PKCE S256 用） */
async function sha256Base64Url(input: string): Promise<string> {
  const data = new TextEncoder().encode(input);
  const digest = await crypto.subtle.digest('SHA-256', data);
  return base64UrlEncode(new Uint8Array(digest));
}

export interface OidcSecurityParams {
  /** CSRF 対策の乱数（コールバックで突合） */
  state: string;
  /** IDトークンリプレイ対策の乱数（IDトークンの nonce クレームと突合） */
  nonce: string;
  /** PKCE code_verifier（トークン交換時にバックエンドへ送信） */
  codeVerifier: string;
  /** PKCE code_challenge（S256、認可リクエストに付与） */
  codeChallenge: string;
}

/**
 * state / nonce / PKCE パラメータを生成する。
 */
export async function generateOidcSecurityParams(): Promise<OidcSecurityParams> {
  const state = randomUrlSafe(16);
  const nonce = randomUrlSafe(16);
  // code_verifier は 43〜128 文字。32 バイト → base64url 43 文字
  const codeVerifier = randomUrlSafe(32);
  const codeChallenge = await sha256Base64Url(codeVerifier);
  return { state, nonce, codeVerifier, codeChallenge };
}

/** OIDC フローパラメータの sessionStorage キー */
export const OIDC_STORAGE_KEYS = {
  state: 'oidc_state',
  nonce: 'oidc_nonce',
  codeVerifier: 'oidc_code_verifier',
  provider: 'oidc_provider',
} as const;
